'use client';

import { createClient } from '@/lib/supabase/client';

export interface RfpFormData {
  name: string;
  company: string;
  email: string;
  phone: string;
  projectType: string;
  message: string;
  servicePage?: string;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }
  return false;
}

export const rfpService = {
  async submitInquiry(formData: RfpFormData): Promise<{ success: boolean; error?: string }> {
    const supabase = createClient();

    try {
      const { error } = await supabase.from('rfp_submissions').insert({
        name: formData.name,
        company: formData.company,
        email: formData.email,
        phone: formData.phone,
        project_type: formData.projectType,
        message: formData.message,
        service_page: formData.servicePage || 'homepage',
      });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          return { success: false, error: 'Database configuration error. Please try again later.' };
        } else {
          console.log('Data error:', error.message);
          return { success: false, error: 'Failed to submit inquiry. Please try again.' };
        }
      }

      // Send email notification via Supabase Edge Function (Resend)
      try {
        const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
        const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
        if (supabaseUrl && supabaseAnonKey) {
          await fetch(`${supabaseUrl}/functions/v1/send-contact-email`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${supabaseAnonKey}`,
            },
            body: JSON.stringify({
              name: formData.name,
              company: formData.company,
              email: formData.email,
              phone: formData.phone,
              projectType: formData.projectType,
              message: formData.message,
              servicePage: formData.servicePage || 'homepage',
            }),
          });
        }
      } catch (emailErr) {
        // Email failure is non-blocking — inquiry is already saved
        console.log('Email notification error (non-blocking):', emailErr);
      }

      return { success: true };
    } catch (err: any) {
      console.log('RFP submission error:', err?.message);
      return { success: false, error: 'An unexpected error occurred. Please try again.' };
    }
  },
};
